/**
 * @fileoverview Mock catalog books for Phase 0-5 UI development, before
 * catalogApi.fetchCatalog is wired to the live backend in Phase 6.
 *
 * These are the actual 10 books seeded by backend/scripts/seed_catalog.py,
 * with Layer 2 taxonomy fields (mood_ids, theme_ids, setting_ids,
 * period_ids, cultural_concept_ids) exactly as applied by
 * backend/scripts/seed_layer2_taxonomy.py. Only 3 of the 10 seeded books
 * (aozora-kokoro, aozora-rashomon, catalog-1q84) currently have Layer 2
 * tags — the rest have empty arrays for those fields, matching the real
 * database state, not invented values.
 *
 * Deviation from the Phase 0 brief: it asked for "No Longer Human" and
 * "Run Melos" as examples, plus one N5 book. Neither of those two titles
 * exists in the real seeded catalog, and no seeded book is JLPT N5 — the
 * closest real beginner title is aozora-gongitsune (N4, difficulty
 * "beginner"). Using the real 10-book seed set here instead of inventing
 * data for titles that don't exist yet.
 *
 * @type {import('../types/catalogTypes').BookCatalogItem[]}
 */
export const MOCK_CATALOG_BOOKS = [
  {
    id: 'aozora-kokoro',
    title_jp: 'こころ',
    title_en: 'Kokoro',
    title_romaji: 'Kokoro',
    author_name: 'Natsume Soseki',
    author_name_jp: '夏目漱石',
    cover_image: null,
    // description_short is not part of BookCatalogItem (list-view shape) —
    // only BookCatalogDetail carries it, per the real API split. Added
    // here, on this one entry only, because Phase 2's RecommendationCard
    // needs a description and this is the featured book. Real seeded
    // value from backend/scripts/seed_catalog.py, not a placeholder.
    description_short: 'A meditation on isolation and guilt in early 20th-century Japan.',
    genre_ids: ['genre_novel', 'genre_classic'],
    difficulty: 'intermediate',
    jlpt_level: 'N2',
    language: 'ja',
    length_category: 'medium',
    page_count: 248,
    availability: 'free',
    publication_year: 1914,
    rating_avg: 4.7,
    rating_count: 142,
    popularity_score: 3841,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: ['mood_melancholic', 'mood_reflective'],
    theme_ids: ['theme_isolation', 'theme_identity', 'theme_death'],
    setting_ids: ['setting_tokyo', 'setting_historical_jp'],
    period_ids: ['period_meiji'],
    cultural_concept_ids: ['concept_mono_no_aware'],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'aozora-bocchan',
    title_jp: '坊っちゃん',
    title_en: 'Botchan',
    title_romaji: 'Botchan',
    author_name: 'Natsume Soseki',
    author_name_jp: '夏目漱石',
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_classic'],
    difficulty: 'beginner',
    jlpt_level: 'N3',
    language: 'ja',
    length_category: 'short',
    page_count: 90,
    availability: 'free',
    publication_year: 1906,
    rating_avg: 4.5,
    rating_count: 98,
    popularity_score: 2210,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'aozora-rashomon',
    title_jp: '羅生門',
    title_en: 'Rashomon',
    title_romaji: 'Rashōmon',
    author_name: 'Akutagawa Ryunosuke',
    author_name_jp: '芥川龍之介',
    cover_image: null,
    genre_ids: ['genre_short_story', 'genre_classic'],
    difficulty: 'advanced',
    jlpt_level: 'N1',
    language: 'ja',
    length_category: 'short',
    page_count: 20,
    availability: 'free',
    publication_year: 1915,
    rating_avg: 4.8,
    rating_count: 210,
    popularity_score: 5120,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: ['mood_dark', 'mood_tense', 'mood_unsettling'],
    theme_ids: ['theme_survival', 'theme_justice', 'theme_betrayal'],
    setting_ids: ['setting_historical_jp', 'setting_kyoto'],
    period_ids: ['period_heian'],
    cultural_concept_ids: ['concept_aware'],
    award_ids: [],
    adaptation_types: ['adaptation_film'],
  },
  {
    id: 'gutenberg-1342',
    title_jp: '高慢と偏見',
    title_en: 'Pride and Prejudice',
    title_romaji: null,
    author_name: 'Jane Austen',
    author_name_jp: null,
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_romance'],
    difficulty: 'advanced',
    jlpt_level: null,
    language: 'en',
    length_category: 'long',
    page_count: 432,
    availability: 'free',
    publication_year: 1813,
    rating_avg: 4.6,
    rating_count: 340,
    popularity_score: 6700,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'catalog-snow-country',
    title_jp: '雪国',
    title_en: 'Snow Country',
    title_romaji: 'Yukiguni',
    author_name: 'Kawabata Yasunari',
    author_name_jp: '川端康成',
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_romance'],
    difficulty: 'advanced',
    jlpt_level: 'N1',
    language: 'ja',
    length_category: 'medium',
    page_count: 175,
    availability: 'buy',
    publication_year: 1948,
    rating_avg: 4.3,
    rating_count: 54,
    popularity_score: 890,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'catalog-1q84',
    title_jp: '1Q84',
    title_en: '1Q84',
    title_romaji: null,
    author_name: 'Murakami Haruki',
    author_name_jp: '村上春樹',
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_contemporary'],
    difficulty: 'intermediate',
    jlpt_level: 'N2',
    language: 'ja',
    length_category: 'long',
    page_count: 925,
    availability: 'buy',
    publication_year: 2009,
    rating_avg: 4.4,
    rating_count: 410,
    popularity_score: 8200,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: ['mood_mysterious', 'mood_dreamlike', 'mood_suspenseful'],
    theme_ids: ['theme_identity', 'theme_love', 'theme_dreams'],
    setting_ids: ['setting_tokyo', 'setting_contemporary_jp'],
    period_ids: ['period_heisei'],
    cultural_concept_ids: ['concept_ma'],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'aozora-sanshiro',
    title_jp: '三四郎',
    title_en: 'Sanshiro',
    title_romaji: 'Sanshirō',
    author_name: 'Natsume Soseki',
    author_name_jp: '夏目漱石',
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_classic'],
    difficulty: 'intermediate',
    jlpt_level: 'N3',
    language: 'ja',
    length_category: 'medium',
    page_count: 260,
    availability: 'free',
    publication_year: 1908,
    rating_avg: 4.1,
    rating_count: 42,
    popularity_score: 610,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'aozora-gongitsune',
    title_jp: 'ごん狐',
    title_en: 'Gon, the Little Fox',
    title_romaji: 'Gongitsune',
    author_name: 'Niimi Nankichi',
    author_name_jp: '新美南吉',
    cover_image: null,
    genre_ids: ['genre_childrens', 'genre_short_story'],
    difficulty: 'beginner',
    jlpt_level: 'N4',
    language: 'ja',
    length_category: 'short',
    page_count: 15,
    availability: 'free',
    publication_year: 1932,
    rating_avg: 4.9,
    rating_count: 76,
    popularity_score: 1450,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'catalog-kitchen',
    title_jp: 'キッチン',
    title_en: 'Kitchen',
    title_romaji: 'Kicchin',
    author_name: 'Yoshimoto Banana',
    author_name_jp: '吉本ばなな',
    cover_image: null,
    genre_ids: ['genre_novel', 'genre_contemporary'],
    difficulty: 'intermediate',
    jlpt_level: 'N2',
    language: 'ja',
    length_category: 'medium',
    page_count: 152,
    availability: 'buy',
    publication_year: 1988,
    rating_avg: 4.2,
    rating_count: 88,
    popularity_score: 1980,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
  {
    id: 'aozora-tsurezuregusa',
    title_jp: '徒然草',
    title_en: 'Essays in Idleness',
    title_romaji: 'Tsurezuregusa',
    author_name: 'Yoshida Kenko',
    author_name_jp: '吉田兼好',
    cover_image: null,
    genre_ids: ['genre_essay', 'genre_classic'],
    difficulty: 'native',
    jlpt_level: null,
    language: 'ja',
    length_category: 'medium',
    page_count: 200,
    availability: 'free',
    publication_year: 1330,
    rating_avg: 3.9,
    rating_count: 21,
    popularity_score: 310,
    import_status: 'not_imported',
    is_on_shelf: false,
    mood_ids: [],
    theme_ids: [],
    setting_ids: [],
    period_ids: [],
    cultural_concept_ids: [],
    award_ids: [],
    adaptation_types: [],
  },
];
